import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  Plus, ArrowRight, AlertTriangle, LayoutGrid, Folder, ClipboardList, Trash2,
  Search, Loader2, X,
} from '../../icons';
import type { TeamDto } from '../../api/project-teams';
import type { MyTaskDto, TaskProjectDto } from '../../api/tasks';
import type { ProjectHomeSummaryDto, ProjectSearchResultDto } from '../../api/project-home';
import { searchProjectHome } from '../../api/project-home';
import { useAuthStore } from '../../store/authStore';
import { useFavoritesStore } from '../../store/favoritesStore';
import { FavoriteStar } from '../../components/Favorites';
import { Avatar, Combobox, Skeleton } from '../../components/shared';
import { liveProjects, spaceProjectCount } from './space-cards';
import { canDeleteSpace, confirmAndDeleteSpace } from './confirmDeleteSpace';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './ProjectHomeSpaces.css';

/**
 * ProjectHomeSpaces (AW-001 · 2026-08-18) — 프로젝트메인 개선안.
 *
 * 설계 SSOT: 화면설계서 v2.2 `AW-001` (프로젝트 > 내 팀, Page No.1).
 *   1. 실재하지 않는 지표 제거 — 결재대기·오늘미팅 → 마감임박·지연·위험
 *   2. 진행률을 도넛 링으로
 *   3. 스페이스별 고유 색
 *   4. 숫자 중심 히어로 유지
 *   5. 검색을 '프로젝트 통합 검색' 으로 재정의
 *   6. 내 스페이스 = 내가 진행 중인 프로젝트가 있는 스페이스만
 *
 * AW-005 (2026-08-25) — 홈이 약속한 것을 실제로 하게 만든 보강. 기존 구조·클래스는 그대로 두고
 * 아래만 더한다(전부 additive):
 *   ① 히어로 숫자를 **누를 수 있게** — 마감임박·지연·위험 → 그 조건으로 걸러진 내 업무 목록.
 *      (AW-001 로 오면서 v2 대시보드에 있던 드릴다운이 사라져 있었다.)
 *   ② 검색을 **진짜 통합 검색**으로 — 서버(`/project-home/search`)가 스페이스·프로젝트·업무를
 *      찾아 주고, 결과에서 바로 연다. 종전에는 이미 받아온 스페이스 카드만 걸러 냈다.
 *   ③ `Ctrl/⌘+K`·`/` 로 검색창 포커스, `Esc` 로 지우기.
 *   ④ '이어서 하기'(최근 방문) · '즐겨찾기' 줄 — 매일 여는 두세 개를 격자에서 찾지 않게.
 *   ⑤ 정렬에 '지연 많은 순 · 진행률 낮은 순' 추가 + 토글 대신 목록(Combobox)으로.
 *   ⑥ 지연 건수는 **서버 집계**(summary.overdueByProject)를 쓴다 — 종전에는 `/tasks/me`
 *      상위 20건에서 세어, 전체 집계인 히어로 숫자와 카드 칩이 서로 어긋났다.
 *   ⑦ 로딩 스켈레톤 — 종전 `—` 는 "0인지 로딩인지" 를 구분해 주지 못했다.
 *   ⑧ 카드에 담당자 아바타 — memberPreview 는 이미 목록 응답에 들어 있다.
 *
 * 데이터는 ⑥ 을 뺀 전부가 기존 API 로 채워진다 — ProjectHomeWidgets 와 동일 원칙.
 *   getProjectHomeSummary · listTeams · listProjects · listMyTasks · searchProjectHome
 *
 * 상단 조직 헤더(알림·아바타·스페이스 생성)는 전역 셸(LayoutShell/TopBar)이 이미
 * 렌더하므로 여기서 다시 만들지 않는다 — 본 컴포넌트는 컨텐츠 영역만 담당한다.
 *
 * ## ⭐⭐ 2026-10-10 — ★ 이 파일은 ⭐ 정본(feature/dev-syk dd05a6fcd)을 ★ «덮은» 것이다
 *
 * ```
 * ⇒ 사용자 지시 ⇒ 「프로젝트 메뉴가 정본이다 · ★ 그냥 ⭐ 그대로 이관해달라」
 * ⇒ ⇒ ★ 그래서 ⭐ 바탕은 ★ 정본 글자 그대로다 ⇒ ⇒ 어긋나는 자리 ★ «넷»만 아래와 같다
 * ```
 *
 * ### ⓐ ★ `daysLeft` ＋ `isOverdue` ⇒ ⭐ «우리 것»을 지킨다
 *
 * ```
 * 정본 ⇒        function daysLeft(due)                    ⇐ export ⛔ 없음 · now ⛔ 없음
 * 우리 ⇒ export function daysLeft(due, now = Date.now())  ⇐ ★ 둘 다 ⭐ 있다
 * ⇒ ⛔ 정본으로 덮으면 ⇒ ★ project-dday-ssot.guard.test.ts:33 의 import 가 ⭐ 깨진다
 *    (#2066 에 머지된 가드 — ⇒ 「날짜 판정이 ★ 한 곳에서 오나」를 잠근다)
 * ⇒ ⚠ 그리고 ★ isOverdue 의 ⭐ 주석 덩이도 지킨다 — ⇒ ★ filter 가 둘째 인자로 «색인»을 넘겨
 *    ⇒ ⇒ 1970년이 되어 ⭐ «모든 업무가 지연»이 될 자리를 ★ 그 글이 막는다(돌려서 확인해 둔 것)
 * ⇒ ✅ 정본이 ★ «더한» 문단(「폴백 경로 전용」)은 ⇒ ⭐ 받았다 — ⇒ 둘을 ★ 이어 붙였다
 * ```
 *
 * ### ⓑ ★ 검색창 ⇒ ⭐ 부품을 쓴다 (정본은 인라인)
 *
 * ```
 * ⇒ 2026-10-07 에 ★ 마크업 ＋ CSS 를 ⭐ ProjectSearchBox 로 떼어 냈다 ⇒ ⇒ 쓰는 곳이 ★ 셋이다
 * ⇒ ⇒ ⛔ 정본은 그 refactor 를 모른다 ⇒ ⇒ 덮어서 그대로 적으면 ★ «두 벌»이 된다
 *    ⇒ ⇒ ⇒ ProjectHomeSpaces.css:112 가 ★ 그것을 ⭐ 이미 ⛔ 금지해 두었다
 * ⇒ ✅ 정본이 더한 셋(스피너 · ✕ 지우기 · Ctrl+K·`/`·Esc)은 ⇒ ⭐ 부품의 «선택 칸»으로 받았다
 *    ⇒ ⇒ ★ 그려지는 class 이름은 ⭐ 정본과 같다 ⇒ ⇒ 모양·동작이 ★ 같아진다
 * ⇒ ⇒ ★ 그래서 ⭐ 이 파일에서 `Search`·`X`·`Loader2` 아이콘과 `searchRef` 가 ★ 빠졌다
 *    ⇒ ⇒ ⚠ 셋 다 ★ 정본에서 ⭐ «검색창에만» 쓰였다(정본 :523 · :549 · :541 — ⇒ 각 1 곳)
 * ```
 *
 * ### ⓒ ⚠ 선택 props 다섯은 ★ 아직 ⭐ «안 넘어온다»
 *
 * ```
 * listsLoading · onOpenTask · onOpenStat · onOpenPath · onSpaceDeleted  ⇒ ★ 전부 `?` 선택
 * ⇒ 우리 ProjectPage.tsx:176 은 ★ 필수 8 개만 넘긴다 ⇒ ⇒ ✅ 그래서 ★ tsc 가 통과한다
 * ⇒ ⇒ ⭐ 안 넘어온 칸은 ★ 정본이 ⭐ «스스로 가려 둔다»:
 *      onSpaceDeleted 없음 ⇒ 🗑 단추를 ⛔ 그리지 않는다 (⇐ 갱신 경로 없이 되돌릴 수 없는 동작을 안 낸다)
 *      onOpenStat 없음     ⇒ 히어로 숫자는 ★ 누를 수 없는 글자로 남는다
 *      onOpenPath 없음     ⇒ 최근·즐겨찾기 칩을 눌러도 ★ 아무 일도 안 한다
 *      listsLoading 없음   ⇒ 스켈레톤 대신 ★ 빈 격자 (false 기본값)
 * ⇒ ⏳ 그 다섯을 잇는 것은 ★ 다음 판이다 — ⇒ ⭐ SpaceCreateModal·refreshLists 가 ★ 먼저 서야 한다
 * ```
 *
 * ### ⓓ ★ 서버 집계 ⇒ ⭐ 두 칸을 받는 쪽에 들였다
 *
 * ```
 * summary.myTasks.riskCount · summary.overdueByProject ⇒ ★ api/project-home.ts 에 ⭐ 더했다
 * ⇒ ⭐ 둘 다 ★ 서버가 «이미 보낸다» ⇒ ⇒ 실측은 ★ 그 파일 머리말에 적었다
 * ⇒ ⇒ ⚠ riskCount 는 ★ 우리 종전 주석이 ⭐ 기다리던 자리다:
 *    「(백엔드에 riskCount 가 생기면 ★ 이 파생만 걷어내면 된다.)」 ⇒ ⇒ ✅ 이 판이 ★ 그 걷어내기다
 * ```
 */

/** 스페이스 구분용 팔레트 — 상태색(초록·파랑·주황·빨강)과 역할이 겹치지 않게 분리(설계서 v2.1). */
const SPACE_ACCENTS = [
  'var(--pj-space-accent-1)', 'var(--pj-space-accent-2)', 'var(--pj-space-accent-3)', 'var(--pj-space-accent-4)',
  'var(--pj-space-accent-5)', 'var(--pj-space-accent-6)', 'var(--pj-space-accent-7)', 'var(--pj-space-accent-8)',
] as const;

/**
 * id → 팔레트 인덱스. 목록 순서가 바뀌어도 같은 대상은 같은 색을 유지한다.
 * 2026-08-21 — 프로젝트 카드도 색이 지정되지 않았을 때 같은 팔레트로 떨어지도록 export.
 */
export function accentOf(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return SPACE_ACCENTS[h % SPACE_ACCENTS.length]!;
}

function startOfDay(d: Date): number {
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * 남은 일수. 과거면 음수. 날짜가 없거나 파싱 불가면 null.
 *
 * ## ⚠⚠ 2026-10-09 — ★ 이것은 ⭐ `my-task-due.ts` 의 `dueDayDiff` «사본»이다
 *
 * ```
 * SSOT  my-task-due.ts:43-50   dueDayDiff(dueDate, now = Date.now())
 *   a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
 *   b = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate())
 *   Math.round((a - b) / 86_400_000)
 * 여기  startOfDay(d) ＝ Date.UTC(d.getFullYear(), …)  ＋  Math.round((… - …) / 86_400_000)
 * ⇒ ⇒ ✅ 2026-10-09 에 맞대어 ★ 「셈이 ⭐ 같다」를 확인했다 — ⇒ null 경계도 같다
 *   (여기: `?? 1` ⇒ `< 0` 거짓 · SSOT: dueDate 없으면 null ⇒ 'overdue' 아님)
 * ```
 * ⇒ ⚠ 그런데 ★ 그것은 ⭐ «오늘» 같다는 뜻이고 ⇒ ⇒ ★ 「다음 고침에서 ⭐ 갈라진다」가 위험이다.
 *   ⇒ ⇒ ⭐ 그래서 ★ 둘이 ⭐ «같은 답을 내나»를 ⇒ ★ 시험이 잠근다
 *     (`pages/ProjectPage/project-dday-ssot.guard.test.ts`).
 *   ⇒ ⛔ 사본을 ★ «지우지는» 않는다 — ⇒ 그것은 ⭐ 렌더 경로를 바꾸는 일이고
 *     ⇒ ⇒ 이 레포의 「우회·additive 우선」에 어긋난다(관제 결정 2026-10-09).
 *
 * ⇒ ⭐ `now` 는 ★ 2026-10-09 에 ⭐ «더한» 기본 인자다 — ⇒ ★ 시험이 시계를 고정할 수 있게.
 *   ⇒ ⇒ ★ 안 주면 ⭐ 종전과 «한 글자도» 다르지 않다(⇒ 호출자 `:62`·`:161` 둘 다 무변경).
 *   ⇒ ⇒ ⚠ 그리고 ★ SSOT 가 ⭐ 쓰는 꼴과 ★ 같아진다(그쪽은 기본 인자가 넷).
 */
export function daysLeft(due: string | null | undefined, now: number = Date.now()): number | null {
  if (!due) return null;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((startOfDay(d) - startOfDay(new Date(now))) / 86_400_000);
}

/**
 * '지연' 판정 — 마감이 지났고 **아직 끝나지 않은** 할일.
 *
 * DONE 을 빼지 않으면 이미 끝낸 일까지 지연·위험으로 세어, 진행률 100% 인 스페이스에
 * 지연 칩이 붙는 자기모순이 화면에 그대로 드러난다. 서버 집계(`summary.myTasks.overdue`)
 * 는 미완료만 세므로 히어로 숫자와 카드 칩이 서로 어긋나기도 한다.
 *
 * ## ⛔⛔ 2026-10-09 — ★ 이 함수에 ⭐ `now` 인자를 ★ 더하지 ⛔ «말 것»
 *
 * ```
 * ⇒ ★ 이 함수는 ⭐ `:187` 에서 ★ ⭐ `tasksOfTeam.filter(isOverdue)` 로 «맨손으로» 넘겨진다.
 * ⇒ ⇒ ⛔⛔ `Array.prototype.filter` 는 콜백에 ★ ⭐ `(원소, «색인», 배열)` 을 넘긴다
 *   ⇒ ⇒ ★ 그래서 ⭐ `isOverdue(t, now = Date.now())` 로 바꾸면 ⇒ ★ 둘째 인자로 ⭐ «색인»이 들어가
 *     ⇒ ⇒ ⇒ ⭐ 첫 줄은 `now = 0`(⇒ 1970년) · 둘째는 1 · … 로 재게 된다
 *       ⇒ ⇒ ⇒ ⛔ 그러면 ★ 모든 업무가 ⭐ «지연»으로 보인다(⇒ 기한이 1970년보다 뒤니까)
 * ⇒ ★ 돌려서 확인했다(2026-10-09):
 *     function g(x, now = 9999) { seen.push(now); return true; }
 *     [10, 20, 30].filter(g)   ⇒ seen ＝ ⭐ [0, 1, 2]      ⇐ ★ 기본값이 «덮인다»
 * ```
 * ⇒ ✅ 그래서 ★ 시계 고정은 ⭐ `daysLeft` 의 기본 인자와 ⇒ ★ 시험의 ⭐ «가짜 타이머»로 한다.
 *   ⇒ ⇒ ⭐ 즉 ★ 이 함수는 ⭐ «한 인자»로 둔다. ⇒ ⇒ ⚠ 꼭 더해야 하면 ★ `:187` 을 먼저
 *     ⇒ `(t) => isOverdue(t)` 로 고쳐라 — ⇒ ⛔ 순서를 거꾸로 하면 ★ 조용한 결함이 된다.
 *   ⇒ ⇒ ★ 그 불변식을 ⭐ 가드가 잠근다(`project-dday-ssot.guard.test.ts`).
 *
 * ⚠ AW-005 이후 이 함수는 **폴백 경로 전용**이다 — 평상시 지연 건수는 서버 집계를 쓴다.
 *   (구버전 서버 응답이거나 summary 조회가 실패했을 때만 여기로 떨어진다.)
 *   ⇒ ⭐ 2026-10-10 ⇒ ★ 그 집계는 ⇒ ⭐ 서버가 «이미 보낸다»(api/project-home.ts 머리말에 실측)
 *      ⇒ ⇒ ⚠ 그러니 ★ 이 갈래가 돈다고 ⇒ ⛔ 서버 결함이 아니다 — ★ 보험이다
 */
export function isOverdue(t: MyTaskDto): boolean {
  return t.status !== 'DONE' && (daysLeft(t.dueDate) ?? 1) < 0;
}

function ddayLabel(n: number): string {
  if (n === 0) return tProject('project.home.aw001.dday_today');
  if (n > 0) return tProject('project.home.aw001.dday_left', { n });
  return tProject('project.home.aw001.dday_over', { n: -n });
}

function greetingKey(): 'project.home.aw001.greeting_morning'
| 'project.home.aw001.greeting_afternoon'
| 'project.home.aw001.greeting_evening' {
  const h = new Date().getHours();
  if (h < 12) return 'project.home.aw001.greeting_morning';
  if (h < 18) return 'project.home.aw001.greeting_afternoon';
  return 'project.home.aw001.greeting_evening';
}

/**
 * 도넛 링 진행률 (설계서 개선 2). 얇은 바 대신 링 — 카드를 훑기만 해도 진행 상황이 읽힌다.
 * 2026-08-21 — '내 프로젝트' 카드 격자(ProjectHomeProjects)도 같은 링을 쓰도록 export.
 */
export function ProgressRing({ pct, size = 62, stroke = 7 }: { pct: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const filled = (c * Math.min(Math.max(pct, 0), 100)) / 100;
  return (
    <div
      className="pj-space-ring"
      role="img"
      aria-label={tProject('project.home.aw001.space_progress_aria', { n: pct })}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle className="pj-space-ring-track" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} />
        {/* 0% 면 값 호를 그리지 않는다 — stroke-linecap:round 가 길이 0 에서 점 하나를 남긴다. */}
        {filled > 0 && (
          <circle
            className="pj-space-ring-value"
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            strokeDasharray={`${filled.toFixed(2)} ${(c - filled).toFixed(2)}`}
          />
        )}
      </svg>
      <span className="pj-space-ring-label">{pct}%</span>
    </div>
  );
}

/** 담당자 아바타 묶음 (AW-005 ⑧) — 상위 4명 + "외 N명". preview 가 없으면 아무것도 그리지 않는다. */
export function MemberStack({ members, total }: {
  members: { userId: string; name: string; avatarUrl?: string | null }[];
  total?: number;
}) {
  if (members.length === 0) return null;
  const head = members.slice(0, 4);
  const more = Math.max((total ?? members.length) - head.length, 0);
  return (
    <span className="pj-space-avatars" aria-hidden>
      {head.map((m) => (
        <span className="pj-space-avatar" key={m.userId}>
          <Avatar userId={Number(m.userId)} name={m.name} src={m.avatarUrl ?? null} size="xs" />
        </span>
      ))}
      {more > 0 && (
        <span className="pj-space-avatar pj-space-avatar--more">
          {tProject('project.home.aw005.members_more', { n: more })}
        </span>
      )}
    </span>
  );
}

/** 화면에 뿌릴 스페이스 1건 — teams + projects + myTasks 를 합쳐 만든 파생 모델. */
interface SpaceVm {
  id: string;
  name: string;
  memberCount: number;
  projectCount: number;
  progress: number;
  overdue: number;
  accent: string;
  createdAt: string;
  /** 카드에 세울 담당자 아바타 — 그 스페이스 프로젝트들의 memberPreview 를 합쳐 중복 제거한 것. */
  members: { userId: string; name: string; avatarUrl?: string | null }[];
  /** 검색 매칭용 — 팀명 + 프로젝트명 + 태스크 제목을 소문자로 이어 붙인 것. */
  haystack: string;
  /** 2026-08-31 — 삭제 버튼을 띄울지(리더만). 서버 권한(LEADER only)의 UI 미러. */
  deletable: boolean;
  /** 2026-08-31 — 삭제 확인창에 보여 줄 '함께 지워질' 프로젝트 이름들. */
  projectNames: string[];
}

type SortKey = 'recent' | 'name' | 'overdue' | 'progress';

/** 히어로 숫자가 여는 필터 — MyTasksDetailPage 의 `?filter=` 와 같은 값. */
export type HeroStatFilter = 'dueSoon' | 'overdue' | 'risk';

export interface ProjectHomeSpacesProps {
  summary: ProjectHomeSummaryDto | null;
  summaryLoading: boolean;
  teams: TeamDto[];
  projects: TaskProjectDto[];
  myTasks: MyTaskDto[];
  onOpenTeam: (teamId: string) => void;
  onOpenProject: (projectId: string) => void;
  onCreateSpace: () => void;
  /** AW-005 — 팀·프로젝트 목록이 아직 오지 않았는지. 스켈레톤 카드를 세울지 판단한다. */
  listsLoading?: boolean;
  /** AW-005 — 검색 결과의 업무를 연다. 미지정이면 그 업무의 프로젝트를 여는 것으로 폴백. */
  onOpenTask?: (taskId: string, projectId: string) => void;
  /** AW-005 — 히어로 숫자 드릴다운. 미지정이면 숫자는 종전대로 글자로만 남는다. */
  onOpenStat?: (filter: HeroStatFilter) => void;
  /** AW-005 — 최근 방문·즐겨찾기 칩이 가리키는 내부 경로로 이동. */
  onOpenPath?: (to: string) => void;
  /**
   * 2026-08-31 — 스페이스 카드의 [🗑] 로 삭제가 끝난 뒤. 부모가 목록을 다시 읽어
   * 지워진 카드를 걷어 낸다. 미지정이면 버튼 자체를 그리지 않는다(되돌릴 수 없는 동작을
   * 화면 갱신 경로 없이 노출하지 않기 위해).
   */
  onSpaceDeleted?: (spaceId: string) => void;
}

export function ProjectHomeSpaces({
  summary, summaryLoading, teams, projects, myTasks,
  onOpenTeam, onOpenProject, onCreateSpace,
  listsLoading = false, onOpenTask, onOpenStat, onOpenPath, onSpaceDeleted,
}: ProjectHomeSpacesProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const userName = useAuthStore((s) => s.user?.name) ?? '';
  const [query, setQuery] = useState('');
  /*
   * ★ 입력칸을 직접 쥔다 — 정본 ProjectHomeSpaces.tsx:223 (2026-10-12 되돌림).
   *   단축키(⑥)와 지우기 단추가 이 ref 로 포커스를 되돌린다.
   */
  const searchRef = useRef<HTMLInputElement>(null);
  const [sort, setSort] = useState<SortKey>('recent');

  const live = useMemo(() => liveProjects(projects), [projects]);

  /**
   * AW-005 ⑥ — 프로젝트별 지연 건수의 출처.
   *
   * 서버 집계가 있으면 그것을 쓴다. 다만 "지연이 정말 0" 과 "구버전 서버라 필드가 없음" 은
   * 응답만 봐서는 같은 빈 배열이라, 히어로가 지연을 세고 있는데 목록이 비어 있을 때만
   * 옛 파생 경로로 떨어진다.
   */
  const serverOverdueByProject = useMemo(() => {
    if (!summary) return null;
    // 정규화를 거치지 않은 summary 객체(테스트 픽스처·구버전 캐시)도 들어올 수 있다.
    const rows = summary.overdueByProject ?? [];
    if (rows.length === 0 && summary.myTasks.overdue > 0) return null; // 구버전 서버 → 폴백
    const m = new Map<string, number>();
    for (const r of rows) m.set(r.projectId, r.count);
    return m;
  }, [summary]);

  /** 폴백 경로 — `/tasks/me` 로 받아온 범위 안에서만 센다(상위 N건 한정이라 과소집계 가능). */
  const derivedOverdueByProject = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of myTasks) {
      if (!isOverdue(t)) continue;
      m.set(t.project.id, (m.get(t.project.id) ?? 0) + 1);
    }
    return m;
  }, [myTasks]);

  const overdueOfProject = useCallback(
    (projectId: string): number =>
      (serverOverdueByProject ?? derivedOverdueByProject).get(projectId) ?? 0,
    [serverOverdueByProject, derivedOverdueByProject],
  );

  // ① 히어로 지표. 마감임박·지연·위험 모두 서버 집계.
  //    riskCount 가 없는 구버전 서버에서만 종전 클라이언트 파생으로 떨어진다.
  const derivedRisk = useMemo(
    () => myTasks.filter((t) => t.priority === 'HIGH' && isOverdue(t)).length,
    [myTasks],
  );
  const atRisk = summary ? (summary.myTasks.riskCount || derivedRisk) : derivedRisk;

  // ② 가장 급한 건 — 마감일이 가장 가까운 진행 중 프로젝트.
  const urgent = useMemo(() => {
    let best: { p: TaskProjectDto; n: number } | null = null;
    for (const p of live) {
      const n = daysLeft(p.dueDate);
      if (n === null) continue;
      if (!best || n < best.n) best = { p, n };
    }
    return best;
  }, [live]);

  // ③ 내 스페이스 — 내가 속한 스페이스 «전부»(2026-10-13 사용자 결정, 종전 설계서 개선 6 을 뒤집음).
  const spaces = useMemo<SpaceVm[]>(() => {
    const rows: SpaceVm[] = [];
    for (const t of teams) {
      const mine = live.filter((p) => p.teamId === t.id);
      // 카드가 되는 조건은 space-cards.ts 가 단독으로 정한다 — 좌측 네비도 같은 함수를 쓴다.
      const count = spaceProjectCount(t, live);
      /*
       * ⚠ 2026-10-13 — 종전 `if (count === 0) continue;`(진행 중 프로젝트가 없으면 카드 아님)를 걷었다.
       *   사용자 지적: 「스페이스를 클릭하면 내 스페이스에 내가 생성한 스페이스들이 나와야하는데
       *   AxiBrand 하나만 나온다」 ⇒ 방금 만든 스페이스(프로젝트 0)가 홈에서 사라졌다.
       *   사용자 결정 「모두 보이기」 — 정본·08-21 규칙(개선 6)보다 이 결정이 앞선다.
       *   ⇒ 빈 스페이스는 0% · 프로젝트 0개 카드로 선다. count 는 그 «수»로만 쓴다.
       */

      const totalTasks = mine.reduce((a, p) => a + (p.taskCount ?? 0), 0);
      const doneTasks = mine.reduce((a, p) => a + (p.doneTaskCount ?? 0), 0);
      const tasksOfTeam = myTasks.filter((x) => x.project.teamId === t.id);

      // 스페이스의 담당자 = 그 안 프로젝트들의 멤버 미리보기 합집합(중복 제거, 등장 순).
      const seen = new Set<string>();
      const members: SpaceVm['members'] = [];
      for (const p of mine) {
        for (const m of p.memberPreview ?? []) {
          if (seen.has(m.userId)) continue;
          seen.add(m.userId);
          members.push({ userId: m.userId, name: m.name, avatarUrl: m.avatarUrl });
        }
      }

      rows.push({
        id: t.id,
        name: t.name,
        memberCount: t.memberCount,
        projectCount: count,
        progress: totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0,
        // AW-005 — 프로젝트별 서버 집계의 팀 단위 합계.
        overdue: mine.reduce((a, p) => a + overdueOfProject(p.id), 0),
        // 2026-08-19 — 생성 화면에서 고른 색이 있으면 그 색, 없으면 종전 해시 팔레트.
        accent: t.color ?? accentOf(t.id),
        createdAt: t.createdAt,
        members,
        deletable: canDeleteSpace(t),
        projectNames: mine.map((p) => p.name),
        // 검색 힌트가 '프로젝트명 · 담당자 · 태스크로 찾기' 라고 약속하므로 담당자도 넣는다.
        // ⚠ 이 haystack 은 이제 **입력 즉시 보이는 미리 거르기**에만 쓴다 — 진짜 검색은
        //   서버가 한다(AW-005 ②). 클라이언트가 가진 것은 받아온 범위뿐이라서다.
        haystack: [
          t.name,
          ...mine.map((p) => p.name),
          ...members.map((m) => m.name),
          ...tasksOfTeam.map((x) => x.title),
        ]
          .join(' ')
          .toLowerCase(),
      });
    }
    return rows;
  }, [teams, live, myTasks, overdueOfProject]);

  // ④ 정렬 — 검색 중이 아닐 때의 격자 순서.
  const shown = useMemo(() => {
    const rows = [...spaces];
    switch (sort) {
      case 'name': return rows.sort((a, b) => a.name.localeCompare(b.name));
      // 지연·진행률은 1차 키가 같을 때 순서가 흔들리지 않게 이름으로 tie-break 한다.
      case 'overdue': return rows.sort((a, b) => b.overdue - a.overdue || a.name.localeCompare(b.name));
      // 2026-10-13 — 빈 스페이스(프로젝트 0)는 «진행률이 없다» ⇒ 0% 로 맨 앞에 서지 않게 맨 뒤로.
      case 'progress': return rows.sort((a, b) =>
        Number(a.projectCount === 0) - Number(b.projectCount === 0)
        || a.progress - b.progress || a.name.localeCompare(b.name));
      default: return rows.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    }
  }, [spaces, sort]);

  /**
   * 2026-08-31 — 카드의 [🗑] 삭제. 확인창에서 함께 지워질 프로젝트를 먼저 보여 주고,
   * 지워지면 부모가 목록을 다시 읽는다(onSpaceDeleted). 진행 중에는 다른 카드의 삭제를
   * 받지 않는다 — 확인창이 두 개 겹치면 어느 스페이스를 지우는지 알 수 없다.
   */
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const handleDeleteSpace = useCallback(async (s: SpaceVm) => {
    if (deletingId !== null) return;
    setDeletingId(s.id);
    try {
      const ok = await confirmAndDeleteSpace({
        id: s.id,
        name: s.name,
        projectNames: s.projectNames,
        projectCount: s.projectCount,
      });
      if (ok) onSpaceDeleted?.(s.id);
    } finally {
      setDeletingId(null);
    }
  }, [deletingId, onSpaceDeleted]);

  // ⑤ 통합 검색 (AW-005 ②) — 250ms 디바운스 + 마지막 응답만 채택.
  const [results, setResults] = useState<ProjectSearchResultDto | null>(null);
  const [searching, setSearching] = useState(false);
  const reqSeq = useRef(0);
  const q = query.trim();

  useEffect(() => {
    if (!q) {
      setResults(null);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const seq = ++reqSeq.current;
    const timer = window.setTimeout(() => {
      void searchProjectHome(q).then((r) => {
        // 늦게 도착한 옛 응답이 새 결과를 덮지 않게 — 타이핑 중엔 흔한 상황이다.
        if (seq !== reqSeq.current) return;
        setResults(r);
        setSearching(false);
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [q]);

  /** 서버 응답이 오기 전에도 화면이 비어 있지 않게 — 이미 가진 스페이스로 먼저 좁힌다. */
  const localMatches = useMemo(
    () => (q ? spaces.filter((s) => s.haystack.includes(q.toLowerCase())) : []),
    [spaces, q],
  );

  /*
   * ⑥ 단축키 (AW-005 ③) — Ctrl/⌘+K 는 어디서든, `/` 는 입력 중이 아닐 때만.
   *   정본 ProjectHomeSpaces.tsx:399-418 글자 그대로다.
   *
   * ## 2026-10-12 — 되돌림 (사용자 ⇒ 「정본대로」)
   *
   * ```
   * 2026-10-07 에 마크업·CSS·단축키를 `ProjectSearchBox` 라는 공용 부품으로 떼어 냈었다.
   * ⇒ 관제가 그때의 사용자 결정을 「정본처럼 홈 «안»에 둔다」로 읽어 그 부품 유지를 허락했다.
   * ⇒ 2026-10-12 사용자가 「정본대로」라고 답해 ⇒ 그 «읽기»를 되돌린다.
   *   ⇒ ⇒ 즉 되돌리는 것은 코드의 잘못이 아니라 «결정을 읽은 방식»이다.
   * ⛔ 그래도 `ProjectSearchBox.tsx` 는 «지우지 않는다» — 쓰는 곳이 셋 남는다
   *   (ProjectPage.tsx 의 3c 갈래 · ProjectSearchPage · 그 CSS). 지우면 롤백 경로가 깨진다.
   * ```
   *
   * ⚠ `typing` 판정(el.tagName === 'INPUT' …)이 없으면 글을 적는 중에도 `/` 가 가로채
   *   사용자가 «슬래시를 못 쓴다». 그래서 정본이 그 줄을 둔 것이다.
   * ⚠ 그리고 `removeEventListener` 를 빠뜨리면 화면을 떠나도 핸들러가 남는다(누수).
   */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (
        el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable
      );
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
        return;
      }
      if (e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ⑦ 즐겨찾기 줄 (AW-005 ④)
  const favorites = useFavoritesStore((s) => s.items);
  // 즐겨찾기는 레일 메뉴까지 함께 담기는 목록이라, 여기서는 프로젝트 쪽 항목만 보여 준다.
  const projectFavorites = useMemo(
    () => favorites.filter((f) => f.to.startsWith('/project/space/') || f.to.startsWith('/task?')),
    [favorites],
  );

  const openPath = useCallback((to: string) => {
    if (onOpenPath) onOpenPath(to);
  }, [onOpenPath]);

  const SORT_OPTIONS = useMemo(() => ([
    { value: 'recent', label: tProject('project.home.aw001.sort_recent') },
    { value: 'name', label: tProject('project.home.aw001.sort_name') },
    { value: 'overdue', label: tProject('project.home.aw005.sort_overdue') },
    { value: 'progress', label: tProject('project.home.aw005.sort_progress') },
  ]), []);

  /** 히어로 숫자 1칸 — onOpenStat 이 없으면 종전처럼 누를 수 없는 글자로 남는다. */
  function HeroStat({ tone, value, label, filter, openLabel }: {
    tone?: 'warn' | 'risk';
    value: number | undefined;
    label: string;
    filter: HeroStatFilter;
    openLabel: string;
  }) {
    const cls = `pj-space-stat${tone ? ` pj-space-stat--${tone}` : ''}`;
    const body = (
      <>
        <span className="pj-space-stat-num">
          {summaryLoading
            ? <Skeleton width="42px" height="30px" className="pj-space-stat-skel" />
            : String(value ?? 0)}
        </span>
        <span className="pj-space-stat-label">{label}</span>
      </>
    );
    if (!onOpenStat || summaryLoading) return <div className={cls}>{body}</div>;
    return (
      <button
        type="button"
        className={`${cls} pj-space-stat--btn`}
        onClick={() => onOpenStat(filter)}
        aria-label={openLabel} data-tip={openLabel}
      >
        {body}
      </button>
    );
  }

  return (
    <div className="pj-spaces">
      <section className="pj-space-hero" aria-label={tProject('project.home.aw001.hero_aria')}>
        <p className="pj-space-hero-greeting">{tProject(greetingKey(), { name: userName })}</p>

        <div className="pj-space-hero-stats">
          <HeroStat
            value={summary?.myTasks.dueSoon7d}
            label={tProject('project.home.aw001.stat_due_soon')}
            filter="dueSoon"
            openLabel={tProject('project.home.aw005.stat_open_due_soon')}
          />
          <HeroStat
            tone="warn"
            value={summary?.myTasks.overdue}
            label={tProject('project.home.aw001.stat_overdue')}
            filter="overdue"
            openLabel={tProject('project.home.aw005.stat_open_overdue')}
          />
          <HeroStat
            tone="risk"
            value={atRisk}
            label={tProject('project.home.aw001.stat_risk')}
            filter="risk"
            openLabel={tProject('project.home.aw005.stat_open_risk')}
          />
        </div>

        <div className="pj-space-hero-callout">
          <p className="pj-space-hero-callout-text">
            {urgent
              ? tProject('project.home.aw001.urgent', {
                space: urgent.p.teamName ?? tProject('project.team.personal'),
                project: urgent.p.name,
                dday: ddayLabel(urgent.n),
              })
              : tProject('project.home.aw001.urgent_none')}
          </p>
          {urgent && (
            <button
              type="button"
              className="pj-space-hero-cta"
              onClick={() => onOpenProject(urgent.p.id)}
            >
              {tProject('project.home.aw001.urgent_go')}
              <ArrowRight size={14} aria-hidden />
            </button>
          )}
        </div>
      </section>

      {/*
        ★ 검색창 — 정본 ProjectHomeSpaces.tsx:522-556 «글자 그대로» (2026-10-12 되돌림).
        ⇒ 2026-10-07 에 ProjectSearchBox 로 떼어 냈던 것을 사용자 답(「정본대로」)에 따라 되돌렸다.
        ⛔ 부품 파일은 지우지 않았다 — 쓰는 곳이 셋 남는다(3c 갈래 · ProjectSearchPage · 그 CSS).
        ⚠ onSubmit 이 없는 것은 정본도 같다 — 이 홈은 «적는 그 자리에서» 카드가 걸러진다.
      */}
      <div className="pj-space-search">
        <Search size={18} aria-hidden />
        <input
          ref={searchRef}
          type="search"
          className="pj-space-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            // Esc — 한 번에 지우고 포커스를 놓는다.
            if (e.key === 'Escape') {
              e.preventDefault();
              setQuery('');
              e.currentTarget.blur();
            }
          }}
          placeholder={tProject('project.home.aw001.search_placeholder')}
          aria-label={tProject('project.home.aw001.search_placeholder')}
        />
        {searching && <Loader2 size={16} className="pj-space-search-spin" aria-hidden />}
        {query && (
          <button
            type="button"
            className="pj-space-search-clear"
            onClick={() => { setQuery(''); searchRef.current?.focus(); }}
            aria-label={tProject('project.home.aw005.search_clear')}
          >
            <X size={16} aria-hidden />
          </button>
        )}
        <span className="pj-space-search-hint">
          {query ? tProject('project.home.aw001.search_hint') : tProject('project.home.aw005.search_shortcut')}
        </span>
      </div>

      {q ? (
        <SearchResults
          q={q}
          searching={searching}
          results={results}
          fallbackSpaces={localMatches}
          onOpenTeam={onOpenTeam}
          onOpenProject={onOpenProject}
          onOpenTask={onOpenTask}
        />
      ) : (
        <>
          {projectFavorites.length > 0 && (
            <section className="pj-space-strip" aria-label={tProject('project.home.aw005.fav_heading')}>
              <h2 className="pj-space-striptitle">
                {tProject('project.home.aw005.fav_heading')}
              </h2>
              <div className="pj-space-chips">
                {projectFavorites.map((f) => (
                  <button
                    type="button"
                    className="pj-space-pill pj-space-pill--fav"
                    key={f.to}
                    onClick={() => openPath(f.to)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </section>
          )}

          <div className="pj-space-sectionhead">
            <h2 className="pj-space-sectiontitle">{tProject('project.home.aw001.spaces_heading')}</h2>
            <div className="pj-space-sortbox">
              <Combobox
                value={sort}
                onChange={(v) => setSort(v as SortKey)}
                options={SORT_OPTIONS}
                ariaLabel={tProject('project.home.aw001.sort_aria')}
              />
            </div>
          </div>

          <div className="pj-space-grid">
            {/* 목록이 아직 오지 않았을 때는 카드 자리를 스켈레톤으로 잡아 둔다 (AW-005 ⑦) —
                빈 격자 뒤 갑자기 카드가 튀어나오면 화면이 한 번 흔들린다. */}
            {listsLoading && spaces.length === 0
              ? Array.from({ length: 4 }, (_, i) => (
                <div className="pj-space-card pj-space-card--skel" key={`skel-${i}`} aria-hidden>
                  <Skeleton width="62px" height="62px" radius="50%" />
                  <Skeleton width="60%" height="18px" />
                  <Skeleton width="45%" height="12px" />
                </div>
              ))
              : shown.map((s) => (
                /*
                  카드는 통째로 버튼이라 그 안에 ☆ 버튼을 넣을 수 없다(버튼 중첩은 무효 마크업).
                  그래서 래퍼를 한 겹 씌우고 별을 **형제**로 얹는다 — 기존 카드 버튼의 마크업·
                  동작·CSS 는 그대로 두고 자리만 빌려주는 방식(2026-08-21).
                */
                <div
                  className={`pj-space-cardwrap${onSpaceDeleted && s.deletable ? ' pj-space-cardwrap--deletable' : ''}`}
                  key={s.id}
                >
                  <button
                    type="button"
                    className="pj-space-card"
                    style={{ '--pj-space-accent': s.accent } as React.CSSProperties}
                    onClick={() => onOpenTeam(s.id)}
                    aria-label={tProject('project.home.aw001.space_open_aria', { name: s.name })}
                  >
                    <span className="pj-space-card-top">
                      <ProgressRing pct={s.progress} />
                      {s.overdue > 0 && (
                        <span className="pj-space-chip pj-space-chip--warn">
                          <AlertTriangle size={12} aria-hidden />
                          {tProject('project.home.aw001.space_overdue', { n: s.overdue })}
                        </span>
                      )}
                    </span>
                    <span className="pj-space-card-name">{s.name}</span>
                    <span className="pj-space-card-meta">
                      {tProject('project.home.aw001.space_meta', { m: s.memberCount, p: s.projectCount })}
                    </span>
                    <MemberStack members={s.members} total={s.memberCount} />
                  </button>
                  <FavoriteStar
                    to={`/project/space/${s.id}`}
                    label={s.name}
                    className="pj-space-fav"
                  />
                  {/*
                    삭제도 별과 같은 방식으로 카드 버튼의 **형제**로 얹는다(버튼 중첩 금지).
                    리더에게만, 그리고 부모가 갱신 경로를 준 경우에만 나온다.
                  */}
                  {onSpaceDeleted && s.deletable && (
                    <button
                      type="button"
                      className="pj-space-del"
                      onClick={() => { void handleDeleteSpace(s); }}
                      disabled={deletingId !== null}
                      aria-label={tProject('project.home.aw001.space_delete_aria', { name: s.name })}
                      data-tip={tProject('project.home.aw001.space_delete_aria', { name: s.name })}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  )}
                </div>
              ))}

            {!listsLoading && (
              <button type="button" className="pj-space-card pj-space-card--new" onClick={onCreateSpace}>
                <Plus size={24} aria-hidden />
                <span>{tProject('project.home.aw001.new_space')}</span>
              </button>
            )}

            {!listsLoading && shown.length === 0 && (
              <p className="pj-space-empty" role="status">
                {tProject('project.home.aw001.empty')}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * 통합 검색 결과 (AW-005 ②) — 스페이스 · 프로젝트 · 업무 세 묶음.
 *
 * 서버 응답을 기다리는 동안에는 이미 가진 스페이스로 좁힌 목록(fallbackSpaces)을 먼저 보여 준다.
 * 빈 화면을 한 박자 띄우는 것보다, 맞는 것 일부라도 즉시 보이는 편이 검색으로 읽힌다.
 */
function SearchResults({
  q, searching, results, fallbackSpaces, onOpenTeam, onOpenProject, onOpenTask,
}: {
  q: string;
  searching: boolean;
  results: ProjectSearchResultDto | null;
  fallbackSpaces: SpaceVm[];
  onOpenTeam: (teamId: string) => void;
  onOpenProject: (projectId: string) => void;
  onOpenTask?: (taskId: string, projectId: string) => void;
}) {
  const spaces = results
    ? results.spaces
    : fallbackSpaces.map((s) => ({
      id: s.id, name: s.name, color: null as string | null, memberCount: s.memberCount, projectCount: s.projectCount,
    }));
  const projects = results?.projects ?? [];
  const tasks = results?.tasks ?? [];
  const nothing = spaces.length === 0 && projects.length === 0 && tasks.length === 0;

  return (
    <section
      className="pj-space-results"
      aria-label={tProject('project.home.aw005.search_results_aria')}
      aria-busy={searching}
    >
      {spaces.length > 0 && (
        <div className="pj-space-resultgroup">
          <h3 className="pj-space-resultheading">
            <LayoutGrid size={14} aria-hidden />
            {tProject('project.home.aw005.group_spaces')}
            <span className="pj-space-resultcount">{spaces.length}</span>
          </h3>
          {spaces.map((s) => (
            <button type="button" className="pj-space-result" key={s.id} onClick={() => onOpenTeam(s.id)}>
              <span
                className="pj-space-resultdot"
                style={{ background: s.color ?? accentOf(s.id) }}
                aria-hidden
              />
              <span className="pj-space-resultname">{s.name}</span>
              <span className="pj-space-resultmeta">
                {tProject('project.home.aw001.space_meta', { m: s.memberCount, p: s.projectCount })}
              </span>
            </button>
          ))}
        </div>
      )}

      {projects.length > 0 && (
        <div className="pj-space-resultgroup">
          <h3 className="pj-space-resultheading">
            <Folder size={14} aria-hidden />
            {tProject('project.home.aw005.group_projects')}
            <span className="pj-space-resultcount">{projects.length}</span>
          </h3>
          {projects.map((p) => (
            <button type="button" className="pj-space-result" key={p.id} onClick={() => onOpenProject(p.id)}>
              <span
                className="pj-space-resultdot"
                style={{ background: p.color ?? accentOf(p.id) }}
                aria-hidden
              />
              <span className="pj-space-resultname">{p.name}</span>
              <span className="pj-space-resultmeta">
                {p.teamName ?? tProject('project.home.aw005.no_space')}
              </span>
            </button>
          ))}
        </div>
      )}

      {tasks.length > 0 && (
        <div className="pj-space-resultgroup">
          <h3 className="pj-space-resultheading">
            <ClipboardList size={14} aria-hidden />
            {tProject('project.home.aw005.group_tasks')}
            <span className="pj-space-resultcount">{tasks.length}</span>
          </h3>
          {tasks.map((t) => (
            <button
              type="button"
              className="pj-space-result"
              key={t.id}
              onClick={() => (onOpenTask ? onOpenTask(t.id, t.project.id) : onOpenProject(t.project.id))}
            >
              <span className="pj-space-resultdot" style={{ background: accentOf(t.project.id) }} aria-hidden />
              <span className="pj-space-resultname">{t.title}</span>
              <span className="pj-space-resultmeta">
                {t.project.name}
                {t.dueDate ? ` · ${t.dueDate}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}

      {nothing && (
        <p className="pj-space-empty" role="status">
          {searching
            ? tProject('project.home.aw005.search_searching')
            : tProject('project.home.aw005.search_none', { q })}
        </p>
      )}
    </section>
  );
}
